import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { ApiError, apiClient, configSigningPath } from '@/lib/api-client';
import type { ConfigSigningState } from '@/types/config-signing';

/** Centralized query keys for the config-signing toggle. */
export const configSigningKeys = {
  all: ['config-signing'] as const,
  state: () => ['config-signing', 'state'] as const,
};

/** GET /admin/config-signing — current enable state + key presence + public keyId. */
export function useConfigSigning(): UseQueryResult<ConfigSigningState, ApiError> {
  return useQuery({
    queryKey: configSigningKeys.state(),
    queryFn: ({ signal }) =>
      apiClient.get<ConfigSigningState>(configSigningPath(), signal),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * PUT /admin/config-signing — flip the DB-backed enable flag. Returns the new
 * state and invalidates the query so the toggle reflects exactly what the API
 * now reports. Clients fail-open, so toggling is safe.
 */
export function useUpdateConfigSigning(): UseMutationResult<
  ConfigSigningState,
  ApiError,
  boolean
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (enabled) =>
      apiClient.put<ConfigSigningState>(configSigningPath(), { enabled }),
    onSuccess: (state) => {
      // Reflect the authoritative new state immediately, then revalidate.
      queryClient.setQueryData(configSigningKeys.state(), state);
      void queryClient.invalidateQueries({ queryKey: configSigningKeys.all });
    },
  });
}
